// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Input } from '@frontend/shadcn/components/ui/input';
import { CheckIcon, XmarkIcon, FileIcon, PenIcon, PlusIcon, MinusIcon } from '@revfluence/fresh-icons/regular/esm';
import { RichTextEditor } from '@frontend/app/refresh-components';
import { useCustomStages } from '../context/CustomStagesContext';

interface Props {
  editedName?: string;
  setEditedName?: (name: string) => void;
  isNew?: boolean;
}

export const StageNameSection: React.FC<Props> = ({
  editedName = 'New Custom Stage',
  setEditedName = () => {},
  isNew = false
}) => {
  const { selectedStage, updateStage } = useCustomStages();
  const [isEditing, setIsEditing] = useState(false);
  const [localName, setLocalName] = useState(editedName);
  const [showDescription, setShowDescription] = useState(false);
  const [description, setDescription] = useState('');

  // Update local name when editedName changes
  useEffect(() => {
    if (editedName) {
      setLocalName(editedName);
    }
  }, [editedName]);

  // Load description from task name when stage changes
  useEffect(() => {
    const taskName = selectedStage?.tasks?.[0]?.name;
    if (taskName) {
      setDescription(taskName);
      setShowDescription(true);
    } else {
      // Reset description when switching to a stage with no task name
      setDescription('');
      setShowDescription(false);
    }
  }, [selectedStage?.id, selectedStage?.tasks]); // Include the entire tasks array as dependency

  const handleSave = () => {
    const trimmedName = localName?.trim() || '';
    if (!trimmedName) return;

    // Always update parent state when saving
    setEditedName(trimmedName);

    if (!isNew && selectedStage) {
      updateStage(selectedStage.id, { name: trimmedName });
    }
    setIsEditing(false);
  };

  const handleDescriptionChange = (newDescription: string) => {
    setDescription(newDescription);
    if (!isNew && selectedStage) {
      const updatedTasks = selectedStage.tasks.map((task, index) => {
        return index === 0 ? { ...task, name: newDescription } : task;
      });
      updateStage(selectedStage.id, { ...selectedStage, tasks: updatedTasks });
    }
  };

  return (
    <div className="p-6 bg-white rounded-2xl border border-gray-200 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-indigo-500 rounded-lg border border-gray-300 flex items-center justify-center">
          <FileIcon className="w-3 h-3 text-white" />
        </div>

        <div className="flex-1 flex items-center gap-2">
          {isEditing ? (
            <>
              <Input
                value={localName || ''}
                onChange={(e) => setLocalName(e.target.value)}
                className="flex-1 h-8 px-3 py-1 text-sm"
                autoFocus
                placeholder="Enter stage name"
              />
              <button
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                onClick={() => {
                  setLocalName(editedName || '');
                  setIsEditing(false);
                }}
              >
                <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
              <button
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                onClick={handleSave}
                disabled={!localName?.trim()}
              >
                <CheckIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
            </>
          ) : (
            <>
              <span className="text-sm font-medium text-gray-800">
                {localName || editedName || 'New Custom Stage'}
              </span>
              <button
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                onClick={() => setIsEditing(true)}
              >
                <PenIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
              <div className="flex-1" />
            </>
          )}
        </div>
      </div>

      {showDescription ? (
        <div className="mt-2">
          <button
            className="h-6 rounded-lg inline-flex justify-start items-center gap-2 mb-2"
            onClick={() => {
              setShowDescription(false);
              setDescription('');
              if (!isNew && selectedStage) {
                const updatedTasks = selectedStage.tasks.map((task, index) => {
                  return index === 0 ? { ...task, name: '' } : task;
                });
                updateStage(selectedStage.id, { ...selectedStage, tasks: updatedTasks });
              }
            }}
          >
            <div className="w-3.5 h-3.5 flex justify-center items-center">
              <MinusIcon className="text-gray-800" />
            </div>
            <div className="flex justify-center items-center gap-2.5">
              <span className="text-gray-800 text-sm font-medium leading-normal">Remove Description</span>
            </div>
          </button>
          <RichTextEditor
            value={description}
            onChange={handleDescriptionChange}
            placeholder="Complete all required fields before submission."
            className="min-h-[100px] w-full border-gray-200 rounded-lg"
            initialValue={description}
          />
        </div>
      ) : (
        <button
          className="flex items-center gap-2 text-sm font-medium text-gray-800 hover:text-gray-600"
          onClick={() => setShowDescription(true)}
        >
          <PlusIcon className="w-3.5 h-3.5" />
          <span>Add description</span>
        </button>
      )}
    </div>
  );
};
